import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboard as dashboardApi } from '@/api/dashboard';
import { analyses as analysesApi } from '@/api/analyses';
import { billing } from '@/api/billing';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import MultiSelect from '@/components/MultiSelect';
import DateRangePicker from '@/components/DateRangePicker';
import { CREDITS_QUERY_KEY } from '@/components/CreditsWidget';
import { actualEffortTone, effortToneAccent, effortToneClass } from '@/lib/effortTone';
import type {
  AuthorAnalytics,
  DashboardFilters,
  ProjectAnalytics,
} from '@/api/types';
import {
  formatHours,
  formatPercent,
  trim,
} from '@/lib/format';
import { formatEstimate, estimatePoint, pertExpected, ESTIMATE_MODE_LABELS, type EstimateMode } from '@/lib/estimate';
import { useEstimateMode } from '@/estimate/EstimateModeContext';
import { useDateRange, applyDateRange } from '@/lib/dateRange';

function repoDisplayName(fullName: string): string {
  const idx = fullName.indexOf('/');
  return idx === -1 ? fullName : fullName.slice(idx + 1);
}

export default function DashboardPage() {
  const { id: orgId, org } = useRequireOrg();
  const { mode, setMode } = useEstimateMode();
  const [searchParams, setSearchParams] = useSearchParams();

  // Default to the last 30 days unless the URL says otherwise. "All time" is ?range=all, so a
  // fresh /dashboard (e.g. clicking the active nav item) always resolves to the 30-day default.
  const { fromDay, toDay, fromIso, toIso } = useDateRange(searchParams);

  const repository = searchParams.getAll('repository');
  const author = searchParams.getAll('author').map(a => a.replace(/^@/, ''));
  // Stable keys for the useMemo deps below — getAll() returns a fresh array each render.
  const repoKey = repository.join('|');
  const authorKey = author.join('|');

  const filters: DashboardFilters = useMemo(() => ({
    from: fromIso,
    to: toIso,
    repository: repository.length ? repository : undefined,
    author: author.length ? author : undefined,
    // Mode drives the accuracy metrics (on-target/success/vs-AI) server-side, so changing it
    // refetches. The estimate columns recompute on the client instantly regardless.
    mode: mode.toUpperCase() as 'OPTIMISTIC' | 'EXPECTED' | 'PESSIMISTIC',
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fromIso, toIso, repoKey, authorKey, mode]);

  const setDateRange = (from: string | null, to: string | null) => {
    const next = new URLSearchParams(searchParams);
    applyDateRange(next, from, to);
    setSearchParams(next);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', orgId, filters],
    queryFn: () => dashboardApi.summary(orgId, filters),
    placeholderData: prev => prev,
  });

  // Reuse the analyses filter endpoint so the dropdowns stay consistent with the PR list.
  const { data: filterOptions } = useQuery({
    queryKey: ['analyses-filters', orgId],
    queryFn: () => analysesApi.filters(orgId),
  });

  const balanceQuery = useQuery({
    queryKey: [...CREDITS_QUERY_KEY, orgId],
    queryFn: () => billing.balance(orgId),
  });
  const showStarterBanner = balanceQuery.data?.balance === 5;

  const updateMulti = useCallback(
    (key: 'repository' | 'author', values: string[]) => {
      const next = new URLSearchParams(searchParams);
      next.delete(key);
      for (const v of values) next.append(key, v);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('repository');
    next.delete('author');
    setSearchParams(next);
  };

  const repoOptions = useMemo(
    () =>
      (filterOptions?.repositories ?? []).map(full => ({
        value: full,
        label: repoDisplayName(full),
      })),
    [filterOptions],
  );

  const authorOptions = useMemo(
    () => (filterOptions?.authors ?? []).map(a => ({ value: a, label: a })),
    [filterOptions],
  );

  const hasActiveFilters = repository.length > 0 || author.length > 0;
  const hours = data?.hoursInRange;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${org.displayName ?? org.githubAccountLogin}`}
      />

      {showStarterBanner ? (
        <div className="starter-banner" role="status">
          <span>
            🎁 You have 5 free credits — open your first pull request to try it. Each
            analyzed PR uses 1 credit.
          </span>
          <Link to="/billing" className="link">
            View billing →
          </Link>
        </div>
      ) : null}

      <section className="filters card">
        <div className="filter-row">
          <label style={{ flex: 1, minWidth: 200 }}>
            <span className="muted small">Project</span>
            <MultiSelect
              options={repoOptions}
              selected={repository}
              onChange={values => updateMulti('repository', values)}
              placeholder="Type to filter projects…"
            />
          </label>
          <label style={{ flex: 1, minWidth: 200 }}>
            <span className="muted small">Developer</span>
            <MultiSelect
              options={authorOptions}
              selected={author}
              onChange={values => updateMulti('author', values)}
              placeholder="Type a github login…"
              trimLeadingChar="@"
            />
          </label>
          {hasActiveFilters ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearFilters}
              aria-label="Clear filters"
            >
              Clear
            </button>
          ) : null}
          <label>
            <span className="muted small">Date range</span>
            <DateRangePicker from={fromDay} to={toDay} onChange={setDateRange} />
          </label>
        </div>
      </section>

      {isLoading ? <Spinner label="Loading metrics…" /> : null}
      {error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}

      {data && hours ? (
        <>
          {(() => {
            const workItemsTotal = data.authorBreakdown.reduce((s, a) => s + a.workItemCount, 0);
            const trackedCount = hours.actualHoursTrackedCount;
            const avgPerItem = trackedCount > 0 ? hours.actualHoursTotal / trackedCount : null;
            return (
              <section className="metric-grid">
                <Metric label="Work items" value={String(workItemsTotal)} hint="Tasks delivered in this slice" />
                <Metric label="Pull requests" value={String(hours.analyzedPullRequests)} hint="Completed PRs feeding those work items" />
                <Metric
                  label="Tracked"
                  value={`${trackedCount}/${workItemsTotal}`}
                  hint="Work items with actual hours recorded"
                />
                <Metric
                  label="Avg / work item"
                  value={avgPerItem == null ? '—' : `${trim(avgPerItem)} h`}
                  hint="Average actual hours per tracked work item"
                />
              </section>
            );
          })()}

          <section className="card">
            <h3>Effort: actual vs estimates</h3>
            <ModeRecommendation
              recommended={data.recommendedMode}
              sample={data.calibrationSampleSize}
              current={mode}
              onApply={setMode}
            />
            <p className="muted small" style={{ marginTop: 0 }}>
              {hours.actualHoursTrackedCount > 0
                ? `Estimates and actuals below are over the ${hours.actualHoursTrackedCount} work item${hours.actualHoursTrackedCount === 1 ? '' : 's'} with actual hours recorded — like-for-like.`
                : 'No actual hours recorded yet. Open a work item from the Analyses page to record actuals.'}
            </p>
            <div className="metric-grid">
              <Metric
                label="Actual hours"
                value={formatHours(hours.actualHoursTotal)}
                accent={effortToneAccent(
                  actualEffortTone(
                    hours.actualHoursTrackedCount > 0 ? hours.actualHoursTotal : null,
                    hours.aiAssistedHoursMax,
                    hours.unassistedHoursMax,
                  ),
                )}
              />
              <Metric
                label="AI-assisted estimate"
                value={formatEstimate(
                  hours.aiAssistedHoursMin,
                  hours.aiAssistedHoursLikely,
                  hours.aiAssistedHoursMax,
                  mode,
                )}
              />
              <Metric
                label="No-AI estimate"
                value={formatEstimate(
                  hours.unassistedHoursMin,
                  hours.unassistedHoursLikely,
                  hours.unassistedHoursMax,
                  mode,
                )}
              />
              {(() => {
                const noAi = estimatePoint(
                  hours.unassistedHoursMin,
                  hours.unassistedHoursLikely,
                  hours.unassistedHoursMax,
                  mode,
                );
                if (hours.actualHoursTrackedCount === 0 || noAi == null) {
                  return (
                    <Metric
                      label="vs no-AI estimate"
                      value="—"
                      hint="Record actual hours on work items to compare against the no-AI estimate."
                    />
                  );
                }
                const saved = noAi - hours.actualHoursTotal;
                return (
                  <Metric
                    label="vs no-AI estimate"
                    value={saved >= 0 ? `Saved ${trim(saved)} h` : `Overran ${trim(-saved)} h`}
                    accent={saved >= 0 ? 'good' : 'warn'}
                    hint={`No-AI ${trim(noAi)} h − actual ${trim(hours.actualHoursTotal)} h (${ESTIMATE_MODE_LABELS[mode].toLowerCase()}).`}
                  />
                );
              })()}
              <Metric
                label="On target"
                value={
                  hours.actualHoursTrackedCount > 0
                    ? `${hours.onTargetCount}/${hours.actualHoursTrackedCount}`
                    : '—'
                }
                hint="Work items whose actual hours fell within the AI-assisted estimate band."
              />
              <Metric
                label="Success rate"
                value={formatPercent(hours.successRate)}
                accent={
                  hours.successRate == null
                    ? undefined
                    : hours.successRate >= 0.7
                    ? 'good'
                    : hours.successRate < 0.4
                    ? 'warn'
                    : undefined
                }
                hint="Share of tracked work items that landed within the AI-assisted estimate."
              />
            </div>
          </section>

          <section className="card">
            <h3>By developer — who's delivering</h3>
            <p className="muted small" style={{ marginTop: 0 }}>
              Throughput (work items), effort per item, how actuals land vs the expected
              estimate, and accuracy. Click a column to sort.
            </p>
            <AuthorBreakdownTable rows={data.authorBreakdown} />
          </section>

          <section className="card">
            <h3>By project</h3>
            <ProjectBreakdownTable rows={data.projectBreakdown} />
          </section>
        </>
      ) : null}
    </>
  );
}

type PerfSortKey = 'developer' | 'workItems' | 'actual' | 'avg' | 'bias' | 'onTarget' | 'success';

/** Derived per-developer performance figures, computed once so we can sort on any of them. */
interface DeveloperPerf {
  author: string;
  workItemCount: number;
  tracked: number;
  actual: number | null;
  avgPerItem: number | null;
  /** actual / expected over tracked work items; >1 = ran over, <1 = came in under. */
  bias: number | null;
  onTargetCount: number;
  successRate: number | null;
}

function toPerf(r: AuthorAnalytics): DeveloperPerf {
  const tracked = r.workItemsWithActual;
  const actual = tracked > 0 ? r.actualHoursTotal : null;
  return {
    author: r.author,
    workItemCount: r.workItemCount,
    tracked,
    actual,
    avgPerItem: tracked > 0 ? r.actualHoursTotal / tracked : null,
    bias: tracked > 0 && r.estimateForTracked > 0 ? r.actualHoursTotal / r.estimateForTracked : null,
    onTargetCount: r.onTargetCount,
    successRate: tracked > 0 ? r.onTargetCount / tracked : null,
  };
}

function perfSortValue(p: DeveloperPerf, key: PerfSortKey): number | string {
  switch (key) {
    case 'developer': return p.author.toLowerCase();
    case 'workItems': return p.workItemCount;
    case 'actual': return p.actual ?? -1;
    case 'avg': return p.avgPerItem ?? -1;
    case 'bias': return p.bias ?? -1;
    case 'onTarget': return p.successRate ?? -1;
    case 'success': return p.successRate ?? -1;
  }
}

function AuthorBreakdownTable({ rows }: { rows: AuthorAnalytics[] }) {
  const [sort, setSort] = useState<{ key: PerfSortKey; dir: 'asc' | 'desc' }>({
    key: 'workItems',
    dir: 'desc',
  });

  const perf = useMemo(() => {
    const list = rows.map(toPerf);
    list.sort((a, b) => {
      const av = perfSortValue(a, sort.key);
      const bv = perfSortValue(b, sort.key);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [rows, sort]);

  if (rows.length === 0) {
    return <p className="muted">No completed PRs in this slice.</p>;
  }

  const toggle = (key: PerfSortKey) =>
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'developer' ? 'asc' : 'desc' },
    );

  const Th = ({ label, k, title }: { label: string; k: PerfSortKey; title?: string }) => {
    const active = sort.key === k;
    return (
      <th
        className={`sortable${active ? ' is-sorted' : ''}`}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        onClick={() => toggle(k)}
        title={title}
      >
        {label}
        <span className="sort-caret" aria-hidden>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </th>
    );
  };

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <Th label="Developer" k="developer" />
            <Th label="Work items" k="workItems" title="Tasks delivered in this slice" />
            <Th label="Actual" k="actual" title="Total tracked hours" />
            <Th label="Avg / item" k="avg" title="Average tracked hours per work item" />
            <Th label="vs AI-assisted estimate" k="bias" title="Logged hours vs the AI-assisted estimate in the current mode. + = over, − = under. Red = over, green = under." />
            <Th label="On target" k="onTarget" title="Tracked work items that landed within the AI-assisted estimate band" />
            <Th label="Success" k="success" title="Share of tracked work items inside the AI-assisted estimate" />
          </tr>
        </thead>
        <tbody>
          {perf.map(p => (
            <tr key={p.author}>
              <td>@{p.author}</td>
              <td className="col-primary">
                <strong>{p.workItemCount}</strong>
              </td>
              <td>
                {p.actual == null ? '—' : `${trim(p.actual)} h`}
                {p.tracked > 0 && p.tracked < p.workItemCount ? (
                  <span className="muted small"> ({p.tracked}/{p.workItemCount})</span>
                ) : null}
              </td>
              <td>{p.avgPerItem == null ? '—' : `${trim(p.avgPerItem)} h`}</td>
              <td><BiasCell bias={p.bias} /></td>
              <td>{p.tracked === 0 ? <span className="muted">—</span> : `${p.onTargetCount}/${p.tracked}`}</td>
              <td><SuccessRate value={p.successRate} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Over/under-run vs the expected estimate. Under (faster) reads good; over reads bad. */
function BiasCell({ bias }: { bias: number | null }) {
  if (bias == null) return <span className="muted">—</span>;
  const delta = Math.round((bias - 1) * 100);
  if (Math.abs(delta) < 5) return <span className="muted small">on est.</span>;
  const cls = delta > 0 ? 'actual-tone-bad' : 'actual-tone-good';
  const label = delta > 0 ? `+${delta}%` : `${delta}%`;
  return (
    <span className={cls} title={delta > 0 ? 'Ran over the AI-assisted estimate' : 'Came in under the AI-assisted estimate'}>
      {label}
    </span>
  );
}

type ProjectSortKey =
  | 'project' | 'workItems' | 'prs' | 'actual' | 'aiEstimate' | 'noAiEstimate' | 'onTarget' | 'success';

function projectSortValue(r: ProjectAnalytics, key: ProjectSortKey): number | string {
  const rate = r.workItemsWithActual > 0 ? r.onTargetCount / r.workItemsWithActual : -1;
  switch (key) {
    case 'project': return repoDisplayName(r.repositoryFullName).toLowerCase();
    case 'workItems': return r.workItemCount;
    case 'prs': return r.pullRequests;
    case 'actual': return r.workItemsWithActual > 0 ? r.actualHoursTotal : -1;
    case 'aiEstimate': return pertExpected(r.aiAssistedHoursMin, r.aiAssistedHoursLikely, r.aiAssistedHoursMax) ?? -1;
    case 'noAiEstimate': return pertExpected(r.unassistedHoursMin, r.unassistedHoursLikely, r.unassistedHoursMax) ?? -1;
    case 'onTarget': return rate;
    case 'success': return rate;
  }
}

function ProjectBreakdownTable({ rows }: { rows: ProjectAnalytics[] }) {
  const { mode } = useEstimateMode();
  const [sort, setSort] = useState<{ key: ProjectSortKey; dir: 'asc' | 'desc' }>({
    key: 'workItems',
    dir: 'desc',
  });

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const av = projectSortValue(a, sort.key);
      const bv = projectSortValue(b, sort.key);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [rows, sort]);

  if (rows.length === 0) {
    return <p className="muted">No completed PRs in this slice.</p>;
  }

  const toggle = (key: ProjectSortKey) =>
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'project' ? 'asc' : 'desc' },
    );

  const Th = ({ label, k, className }: { label: string; k: ProjectSortKey; className?: string }) => {
    const active = sort.key === k;
    return (
      <th
        className={`sortable${active ? ' is-sorted' : ''}${className ? ` ${className}` : ''}`}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        onClick={() => toggle(k)}
      >
        {label}
        <span className="sort-caret" aria-hidden>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </th>
    );
  };

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <Th label="Project" k="project" />
            <Th label="Work items" k="workItems" />
            <Th label="PRs" k="prs" className="col-secondary" />
            <Th label="Actual" k="actual" />
            <Th label="AI-assisted estimate" k="aiEstimate" />
            <Th label="No-AI estimate" k="noAiEstimate" />
            <Th label="On target" k="onTarget" />
            <Th label="Success" k="success" />
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => {
            const rate =
              r.workItemsWithActual > 0
                ? r.onTargetCount / r.workItemsWithActual
                : null;
            const actual = r.workItemsWithActual > 0 ? r.actualHoursTotal : null;
            const toneCls = effortToneClass(
              actualEffortTone(actual, r.aiAssistedHoursMax, r.unassistedHoursMax),
            );
            return (
              <tr key={r.repositoryFullName}>
                <td>{repoDisplayName(r.repositoryFullName)}</td>
                <td className="col-primary">
                  <strong>{r.workItemCount}</strong>
                </td>
                <td className="col-secondary muted small">{r.pullRequests}</td>
                <td className={toneCls}>
                  {r.workItemsWithActual === 0
                    ? '—'
                    : `${trim(r.actualHoursTotal)} h`}
                  {r.workItemsWithActual > 0 &&
                  r.workItemsWithActual < r.workItemCount ? (
                    <span className="muted small">
                      {' '}
                      ({r.workItemsWithActual}/{r.workItemCount})
                    </span>
                  ) : null}
                </td>
                <td>
                  {formatEstimate(r.aiAssistedHoursMin, r.aiAssistedHoursLikely, r.aiAssistedHoursMax, mode)}
                </td>
                <td>
                  {formatEstimate(r.unassistedHoursMin, r.unassistedHoursLikely, r.unassistedHoursMax, mode)}
                </td>
                <td>
                  {r.workItemsWithActual === 0
                    ? '—'
                    : `${r.onTargetCount}/${r.workItemsWithActual}`}
                </td>
                <td>
                  <SuccessRate value={rate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SuccessRate({ value }: { value: number | null }) {
  if (value == null) return <span className="muted">—</span>;
  const pct = Math.round(value * 100);
  const cls =
    value >= 0.7
      ? 'success-rate success-rate-good'
      : value < 0.4
      ? 'success-rate success-rate-warn'
      : 'success-rate';
  return (
    <span className={cls}>
      <span
        className="success-rate-bar"
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        aria-hidden
      />
      <span className="success-rate-label">{pct}%</span>
    </span>
  );
}

function ModeRecommendation({
  recommended,
  sample,
  current,
  onApply,
}: {
  recommended: 'OPTIMISTIC' | 'EXPECTED' | 'PESSIMISTIC' | null;
  sample: number;
  current: EstimateMode;
  onApply: (m: EstimateMode) => void;
}) {
  if (!recommended) return null;
  const rec = recommended.toLowerCase() as EstimateMode;
  const tendency =
    rec === 'pessimistic'
      ? "your team's actuals tend to run over the expected estimate"
      : rec === 'optimistic'
      ? "your team's actuals tend to come in under the expected estimate"
      : 'your team lands close to the expected estimate';
  return (
    <div className="starter-banner" role="status" style={{ marginTop: 0 }}>
      <span>
        💡 Recommended estimate mode: <strong>{ESTIMATE_MODE_LABELS[rec]}</strong> — {tendency}{' '}
        (based on {sample} tracked work item{sample === 1 ? '' : 's'}).
      </span>
      {rec !== current ? (
        <button type="button" className="btn btn-ghost" onClick={() => onApply(rec)}>
          Switch to {ESTIMATE_MODE_LABELS[rec]}
        </button>
      ) : (
        <span className="muted small">In use</span>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: 'good' | 'warn' | 'amber';
  hint?: string;
}) {
  return (
    <div className={`metric ${accent ? `metric-${accent}` : ''}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {hint ? <div className="muted small" style={{ marginTop: '0.25rem' }}>{hint}</div> : null}
    </div>
  );
}
